import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    X, 
    FolderOpen, 
    Trash2, 
    Clock, 
    FileText,
    Layers,
    Loader2,
    ChevronRight,
    Plus
} from 'lucide-react';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const MyProjectsModal = ({ isOpen, onClose, onSelectProject, onNewProject }) => {
    const { isAuthenticated, token, getAuthHeaders } = useAuth();
    const [projects, setProjects] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [deletingId, setDeletingId] = useState(null);

    const fetchProjects = useCallback(async () => {
        if (!isAuthenticated || !token) {
            setIsLoading(false);
            return;
        }
        
        setIsLoading(true);
        try {
            const headers = getAuthHeaders();
            const response = await axios.get(`${BACKEND_URL}/api/projects`, { headers });
            setProjects(response.data);
        } catch (error) {
            console.error('Failed to fetch projects:', error);
            toast.error('Failed to load projects');
        } finally {
            setIsLoading(false);
        }
    }, [isAuthenticated, token, getAuthHeaders]);

    useEffect(() => {
        if (isOpen && isAuthenticated && token) {
            fetchProjects();
        }
    }, [isOpen, isAuthenticated, token, fetchProjects]);

    const handleSelect = async (project) => {
        try {
            const headers = getAuthHeaders();
            const response = await axios.get(`${BACKEND_URL}/api/projects/${project.id}`, { headers });
            onSelectProject(response.data);
            onClose();
        } catch (error) {
            toast.error('Failed to load project');
        }
    };

    const handleDelete = async (id, e) => {
        e.stopPropagation();
        setDeletingId(id);
        try {
            await axios.delete(`${BACKEND_URL}/api/projects/${id}`, {
                headers: getAuthHeaders()
            });
            setProjects(prev => prev.filter(p => p.id !== id));
            toast.success('Project deleted');
        } catch (error) {
            toast.error('Failed to delete project');
        } finally {
            setDeletingId(null);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diff = now - date;
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        
        if (days === 0) return 'Today';
        if (days === 1) return 'Yesterday';
        if (days < 7) return `${days} days ago`;
        return date.toLocaleDateString();
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                onClick={onClose}
            >
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    className="glass-card w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col relative z-10"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="my-projects-modal"
                >
                    {/* Header */}
                    <div className="flex items-center justify-between p-5 border-b border-white/10">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-violet-500/20">
                                <FolderOpen className="w-5 h-5 text-violet-400" />
                            </div>
                            <div>
                                <h3 className="font-heading font-semibold text-lg">My Projects</h3>
                                <p className="text-xs text-gray-500">
                                    {projects.length} project{projects.length !== 1 ? 's' : ''}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            {onNewProject && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                        onNewProject();
                                        onClose();
                                    }}
                                    className="text-xs"
                                >
                                    <Plus className="w-3 h-3 mr-1" />
                                    New
                                </Button>
                            )}
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={onClose}
                                className="hover:bg-white/10"
                            >
                                <X className="w-5 h-5" />
                            </Button>
                        </div>
                    </div>

                    {/* Content */}
                    <ScrollArea className="flex-1">
                        <div className="p-5">
                            {!isAuthenticated ? (
                                <div className="text-center py-12">
                                    <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                                        <FolderOpen className="w-8 h-8 text-gray-500" />
                                    </div>
                                    <p className="text-gray-400 mb-2">Sign in to save projects</p>
                                    <p className="text-sm text-gray-600">
                                        Create an account to save and continue your projects
                                    </p>
                                </div>
                            ) : isLoading ? (
                                <div className="text-center py-12">
                                    <Loader2 className="w-8 h-8 animate-spin text-violet-400 mx-auto mb-4" />
                                    <p className="text-gray-500">Loading projects...</p>
                                </div>
                            ) : projects.length === 0 ? (
                                <div className="text-center py-12">
                                    <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                                        <FolderOpen className="w-8 h-8 text-gray-500" />
                                    </div>
                                    <p className="text-gray-400 mb-2">No projects yet</p>
                                    <p className="text-sm text-gray-600">
                                        Generate a blueprint to create your first project
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {projects.map((project) => (
                                        <motion.div
                                            key={project.id}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            className="p-4 bg-surface rounded-lg border border-white/5 hover:border-violet-500/30 transition-all cursor-pointer group"
                                            onClick={() => handleSelect(project)}
                                            data-testid={`project-${project.id}`}
                                        >
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="flex-1 min-w-0">
                                                    <h4 className="font-medium text-white group-hover:text-violet-300 transition-colors truncate">
                                                        {project.name}
                                                    </h4>
                                                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                                                        {project.original_prompt}
                                                    </p>
                                                    <div className="flex items-center gap-4 mt-3 text-xs text-gray-600">
                                                        <span className="flex items-center gap-1">
                                                            <Clock className="w-3 h-3" />
                                                            {formatDate(project.updated_at)}
                                                        </span>
                                                        {project.has_blueprint && (
                                                            <span className="flex items-center gap-1 text-green-500">
                                                                <FileText className="w-3 h-3" />
                                                                Blueprint
                                                            </span>
                                                        )}
                                                        {project.continue_build_count > 0 && (
                                                            <span className="flex items-center gap-1 text-violet-400">
                                                                <Layers className="w-3 h-3" />
                                                                {project.continue_build_count} expansion{project.continue_build_count !== 1 ? 's' : ''}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Button
                                                        size="icon"
                                                        variant="ghost"
                                                        className="h-8 w-8 opacity-0 group-hover:opacity-100 hover:bg-destructive/20 transition-all"
                                                        onClick={(e) => handleDelete(project.id, e)}
                                                        disabled={deletingId === project.id}
                                                        data-testid={`delete-project-${project.id}`}
                                                    >
                                                        {deletingId === project.id ? (
                                                            <Loader2 className="w-4 h-4 animate-spin" />
                                                        ) : (
                                                            <Trash2 className="w-4 h-4 text-destructive" />
                                                        )}
                                                    </Button>
                                                    <ChevronRight className="w-5 h-5 text-gray-600 group-hover:text-violet-400 transition-colors" />
                                                </div>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </ScrollArea>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};
